/**
 * "Embed in a mobile app (WebView)" — Widget section (ADR-0074).
 *
 * The cheap answer to the mobile-diligence question: the web widget runs
 * unchanged inside a React Native / Flutter WebView, so the agent ships in a
 * native app with no native SDK (the native SDK add-on is the paid path for
 * push / deep-links / background — docs/pricing/add-on-pack-pricing.md § 2.2).
 * The `<script>` block stays inside the real attribute contract — this page is
 * covered by `docs-embed-snippet-truth.test.ts`.
 */
import type { DocPageContent } from '../types';

const HOSTED_PAGE = `<!-- https://help.yourapp.com/assistant — a page you host, loaded by the WebView -->
<!doctype html>
<meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
<style>
  html, body { margin: 0; height: 100%; }
  #assistant { height: 100vh; }
</style>
<div id="assistant"></div>

<script
  type="module"
  src="https://cdn.syncanix.com/widget.js"
  data-key="pk_live_..."
  data-chat-size="embedded"
  data-mount-target="#assistant"
  data-token-provider="getSyncanixToken"
></script>
<script>
  // The widget calls this once per turn; return the token your app injected.
  window.getSyncanixToken = () => window.__SYNCANIX_TOKEN__ ?? undefined;
</script>`;

const REACT_NATIVE = `import { WebView } from 'react-native-webview';

export function AssistantScreen({ userToken }: { userToken: string }) {
  return (
    <WebView
      source={{ uri: 'https://help.yourapp.com/assistant' }}
      // Hand the signed-in user's token to the page before its content loads.
      injectedJavaScriptBeforeContentLoaded={
        \`window.__SYNCANIX_TOKEN__ = \${JSON.stringify(userToken)}; true;\`
      }
      // JavaScript is enabled by default; the widget needs it.
    />
  );
}`;

const FLUTTER = `final controller = WebViewController()
  ..setJavaScriptMode(JavaScriptMode.unrestricted)
  ..setNavigationDelegate(NavigationDelegate(
    onPageStarted: (_) => controller.runJavaScript(
      'window.__SYNCANIX_TOKEN__ = \${jsonEncode(userToken)};',
    ),
  ))
  ..loadRequest(Uri.parse('https://help.yourapp.com/assistant'));

// In build(): return WebViewWidget(controller: controller);`;

export const WIDGET_MOBILE_WEBVIEW_PAGE: DocPageContent = {
  slug: 'widget/mobile-webview',
  titleKey: 'docs.widget.mobile.title',
  descriptionKey: 'docs.widget.mobile.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.mobile.lead' },

    { kind: 'heading', level: 2, id: 'how', textKey: 'docs.widget.mobile.how.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.mobile.how.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.widget.mobile.step.host.title',
          textKey: 'docs.widget.mobile.step.host.text',
        },
        {
          titleKey: 'docs.widget.mobile.step.load.title',
          textKey: 'docs.widget.mobile.step.load.text',
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'page', textKey: 'docs.widget.mobile.page.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.mobile.page.body' },
    { kind: 'code', language: 'html', code: HOSTED_PAGE },
    { kind: 'paragraph', textKey: 'docs.widget.mobile.page.note' },

    { kind: 'heading', level: 2, id: 'react-native', textKey: 'docs.widget.mobile.rn.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.mobile.rn.body' },
    { kind: 'code', language: 'tsx', code: REACT_NATIVE },
    { kind: 'paragraph', textKey: 'docs.widget.mobile.rn.note' },

    { kind: 'heading', level: 2, id: 'flutter', textKey: 'docs.widget.mobile.flutter.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.mobile.flutter.body' },
    { kind: 'code', language: 'dart', code: FLUTTER },

    { kind: 'heading', level: 2, id: 'identity', textKey: 'docs.widget.mobile.identity.heading' },
    {
      kind: 'paragraph',
      textKey: 'docs.widget.mobile.identity.body',
      links: {
        auth: {
          target: { docSlug: 'auth/end-user-auth' },
          labelKey: 'docs.widget.mobile.identity.link',
        },
      },
    },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.mobile.identity.item.token',
        'docs.widget.mobile.identity.item.turn',
        'docs.widget.mobile.identity.item.clear',
      ],
    },

    { kind: 'heading', level: 2, id: 'checklist', textKey: 'docs.widget.mobile.checklist.heading' },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.mobile.checklist.item.origin',
        'docs.widget.mobile.checklist.item.js',
        'docs.widget.mobile.checklist.item.safearea',
        'docs.widget.mobile.checklist.item.keyboard',
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.widget.mobile.native.title',
      textKey: 'docs.widget.mobile.native.body',
      links: {
        pricing: { target: { path: '/pricing' }, labelKey: 'docs.common.link.pricing' },
      },
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.mobile.next.embedding.title',
          textKey: 'docs.widget.mobile.next.embedding.text',
          to: { docSlug: 'widget/embedding' },
        },
        {
          titleKey: 'docs.widget.mobile.next.config.title',
          textKey: 'docs.widget.mobile.next.config.text',
          to: { docSlug: 'widget/configuration' },
        },
      ],
    },
  ],
};
